import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@heroui/react';
import { GripVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CourseFormData } from './schemas';

interface GuidesReorderListProps {
  guides: CourseFormData['guides'];
  onReorder: (guides: CourseFormData['guides']) => void;
  onRemove: (guideId: string) => void;
}

interface SortableGuideItemProps {
  guide: CourseFormData['guides'][number];
  index: number;
  onRemove: (guideId: string) => void;
}

const SortableGuideItem: React.FC<SortableGuideItemProps> = ({ guide, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: guide.guideId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 ${
        isDragging ? 'shadow-lg' : 'hover:shadow-md'
      } transition-shadow`}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <GripVertical className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{index + 1}</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{guide.title}</span>
          {!guide.isPublished && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
              Draft
            </span>
          )}
        </div>
      </div>
      <Button
        isIconOnly
        size="sm"
        variant="light"
        color="danger"
        onPress={() => onRemove(guide.guideId)}
        aria-label="Remove guide"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </motion.div>
  );
};

const GuidesReorderList: React.FC<GuidesReorderListProps> = ({ guides, onReorder, onRemove }) => {
  const { t } = useTranslation('admin');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = guides.findIndex((g) => g.guideId === active.id);
      const newIndex = guides.findIndex((g) => g.guideId === over.id);

      const newGuides = arrayMove(guides, oldIndex, newIndex);
      // Update positions
      const reorderedGuides = newGuides.map((guide, index) => ({
        ...guide,
        position: index + 1,
      }));
      onReorder(reorderedGuides);
    }
  };

  if (guides.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('courses.form.guides.noGuidesAdded')}</div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={guides.map((g) => g.guideId)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {guides.map((guide, index) => (
            <SortableGuideItem key={guide.guideId} guide={guide} index={index} onRemove={onRemove} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default GuidesReorderList;
